// ============================================================================
// stt.ts — ЕДИНЫЙ АДАПТЕР «РЕЧЬ В ТЕКСТ» (SSoT/SRP). Используется везде:
// голосовые сообщения в чате, голосовой ввод в полях форм, реквизиты.
// Сейчас движок — Web Speech API (SpeechRecognition) в браузере: бесплатно,
// без ключей, офлайн в Chrome/Edge. ИНТЕРФЕЙС стабилен — серверный адаптер
// (внешний STT-провайдер) подключается БЕЗ изменения вызовов (замена движка).
// Язык распознавания — из ветки «Языки» (ConfigInfo.language → метаданные
// Language.value, локаль STT; по умолчанию 'ru-RU').
// ============================================================================

import { getSDK } from '@/store/sdkStore';

// Состояние распознавания (для кнопок микрофона).
export type SttStatus = 'idle' | 'listening' | 'processing' | 'error';

export interface SttEngine {
  readonly available: boolean;
  readonly name: string; // 'web-speech'
  start(): void;
  stop(): void;
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

let SpeechRecognitionCtor: (new () => SpeechRecognitionLike) | null = null;
try {
  const w = window as any;
  SpeechRecognitionCtor = w.SpeechRecognition || w.webkitSpeechRecognition || null;
} catch {
  SpeechRecognitionCtor = null;
}

// Кэш локали STT (код языка конфигурации → Language.value).
let sttLocaleCache: string | null = null;
let sttLocalePromise: Promise<string> | null = null;

/**
 * Локаль распознавания из конфигурации: ConfigInfo.language → Language.value
 * (ветка «Языки»); нет — 'ru-RU' (системный «Русский»).
 */
export async function sttLocale(): Promise<string> {
  if (sttLocaleCache) return sttLocaleCache;
  if (sttLocalePromise) return sttLocalePromise;
  sttLocalePromise = (async () => {
    try {
      const cfg: any = await getSDK().config.getConfigInfo({});
      const langCode = String(cfg?.language || '');
      if (langCode) {
        const langs: any = await getSDK().metadata.listLanguages({});
        const hit = (langs?.items || []).find((l: any) => String(l.name || '') === langCode);
        if (hit?.value) {
          sttLocaleCache = String(hit.value);
          return sttLocaleCache;
        }
      }
    } catch {
      /* конфигурация/языки недоступны — дефолт */
    }
    sttLocaleCache = 'ru-RU';
    return sttLocaleCache;
  })();
  return sttLocalePromise;
}

/** Очистить кэш локали (после смены языка конфигурации). */
export function invalidateSttLocale(): void {
  sttLocaleCache = null;
  sttLocalePromise = null;
}

/** Доступен ли Web Speech API в браузере. */
export function sttAvailable(): boolean {
  return SpeechRecognitionCtor !== null;
}

// Доступность голосового ввода/поиска — ЕДИНАЯ проверка useSttAvailability
// (lib/useSttAvailability.ts: permissionCenter + микрофон + SpeechRecognition).
// Синхронный дубль voiceInputAvailable УДАЛЁН (правило свода интеграции-23):
// все кнопки микрофона (формы/профиль/адреса/«Пуск»/поиск списков) читают
// ОДИН хук.

/**
 * SSoT склейки текста диктовки: исходное значение поля + зафиксированный
 * (финальный) текст + текущий interim. Непустые части соединяются одним
 * пробелом; части обрезаются (Chrome отдаёт продолжения фраз с ведущим
 * пробелом « Иван» — иначе двойные пробелы).
 */
export function joinVoiceText(base: string, committed: string, interim: string): string {
  return [base, committed, interim]
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .join(' ');
}

/**
 * Композитор диктовки: накопление финальных порций + текущий interim.
 * СЕМАНТИКА (регресс «ииваИванов…»): финальная порция ДОБАВЛЯЕТСЯ к
 * зафиксированным ОДИН раз (словами, через пробел); interim ПЕРЕЗАПИСЫВАЕТ
 * прежний interim (это ХВОСТ незавершённой фразы — БЕЗ уже зафиксированных
 * слов, как у Chrome: финализированные результаты isFinal=true и в interim
 * не входят); вызывающий получает ПОЛНОЕ значение поля (замена).
 */
export function createVoiceDictationComposer(base: string, onValue: (full: string) => void) {
  let committed = '';
  let interim = '';
  return {
    push(text: string, final: boolean): string {
      if (final) {
        committed = joinVoiceText(committed, text, '');
        interim = '';
      } else {
        interim = text;
      }
      const full = joinVoiceText(base, committed, interim);
      onValue(full);
      return full;
    },
    value(): string {
      return joinVoiceText(base, committed, interim);
    },
  };
}


export interface SttOptions {
  /** Колбэк на каждую порцию распознанного текста (промежуточный/финальный). */
  onText?: (text: string, final: boolean) => void;
  /** Финальный текст (распознавание завершено). */
  onFinal?: (text: string) => void;
  onError?: (message: string) => void;
  /** Явная локаль (переопределяет язык конфигурации). */
  locale?: string;
  /** Распознавать непрерывно (для длинных голосовых). */
  continuous?: boolean;
}

export interface SttSession {
  engine: SttEngine;
  stop(): void;
}

/**
 * Запустить распознавание. Возвращает сессию (stop — остановить). Ошибки
 * доступности движка — ЯВНЫЕ (без фоллбэков).
 */
export async function startStt(opts: SttOptions): Promise<SttSession> {
  const Ctor = SpeechRecognitionCtor;
  if (!Ctor) {
    throw new Error('Распознавание речи недоступно в этом браузере (нужен Chrome/Edge).');
  }
  const locale = opts.locale || (await sttLocale());
  const rec = new Ctor();
  rec.lang = locale;
  rec.interimResults = true;
  rec.maxAlternatives = 1;
  rec.continuous = opts.continuous === true;
  let ended = false;

  // СЕМАНТИКА (регресс «ииваИванов…»): финальные порции — ДЕЛЬТЫ (только
  // результаты от e.resultIndex, ставшие финальными, каждая ОДИН раз);
  // interim — ПОЛНЫЙ текущий незавершённый хвост (замена прежнего interim,
  // не накопление). Раньше кумулятивный итог повторно дошёл всем вызывающим.
  rec.onresult = (e: any) => {
    const resultIndex = typeof e.resultIndex === 'number' ? e.resultIndex : 0;
    let interim = '';
    let finalDelta = '';
    for (let i = 0; i < e.results.length; i++) {
      const r = e.results[i];
      const transcript = r[0]?.transcript || '';
      if (r.isFinal) {
        if (i >= resultIndex) finalDelta += transcript;
      } else {
        interim += transcript;
      }
    }
    if (finalDelta) opts.onText?.(finalDelta, true);
    if (interim) opts.onText?.(interim, false);
    if (finalDelta && !rec.continuous) {
      // Финальная порция без continuous — завершаем
      try { rec.stop(); } catch { /* уже завершено */ }
    }
  };

  rec.onerror = (e: any) => {
    const err = e?.error || 'unknown';
    // network/no-speech — нормальные события; завершаем сессию
    if (err === 'no-speech') return;
    const msg =
      err === 'not-allowed' ? 'Нет доступа к микрофону' :
      err === 'network' ? 'Ошибка сети распознавания' :
      err === 'aborted' ? 'Распознавание прервано' :
      `Ошибка распознавания (${err})`;
    opts.onError?.(msg);
  };

  rec.onend = () => {
    ended = true;
  };

  try {
    rec.start();
  } catch (e: any) {
    throw new Error('Не удалось запустить распознавание: ' + (e?.message || String(e)));
  }

  const engine: SttEngine = {
    available: true,
    name: 'web-speech',
    start: () => { try { rec.start(); } catch { /* уже идёт */ } },
    stop: () => { try { rec.stop(); } catch { /* */ } },
  };
  return { engine, stop: () => { if (!ended) engine.stop(); } };
}
